"use client";

import { Card, CardContent } from "@/components/ui";
import { ProgressBar } from "@/components/ui/progress-bar";
import { Brain, Check, Lightbulb, LoaderCircle, Pencil, Search, Target, Wrench } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

export const InfiniteLoader = ({title, stages, description, estimate, canBeCompleted = false}: {title: string, stages: Array<any>, description: string, estimate: string, canBeCompleted:boolean}) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [completedStages, setCompletedStages] = useState<number[]>([]);
  const [progress, setProgress] = useState(0);

  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const progressInterval = useRef<NodeJS.Timeout | null>(null);

  const progressToNextStage = useCallback(() => {
  if (currentStage < stages.length) {
    const stage = stages[currentStage];
      // Reset progress for new stage
    setProgress(0);
    
    const isLast = stage.id === stages.length - 1;
    // Animate progress during stage duration
    const updateInMS = 100;
    const progressStep = isLast && !canBeCompleted ? 1 : 100 / (stage.duration / updateInMS);
    progressInterval.current = setInterval(() => {
      setProgress(prev => {
        const newProgress = (isLast && !canBeCompleted && prev + progressStep > 99) ? prev : prev + progressStep;
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, updateInMS);

    timeoutId.current = setTimeout(() => {
      if(isLast && !canBeCompleted) {
        // If this is the last stage and cannot be completed, reset to first stage
        return;
      }
      if(progressInterval.current) clearInterval(progressInterval.current);
      setProgress(100);
      setCompletedStages(prev => [...prev, stage.id]);
      setCurrentStage(prev => prev + 1);
    }, stage.duration);
  }
},[canBeCompleted, currentStage, progressInterval, stages, timeoutId]);


  useEffect(() => {
   
    progressToNextStage();

    return () => {
      if (timeoutId?.current) clearTimeout(timeoutId.current);
      if (progressInterval?.current) clearInterval(progressInterval.current);
    };
  }, [currentStage, progressToNextStage]);

  // Get visible stages (previous, current, next)
  const getVisibleStages = () => {
    const visibleStages = [];
    
    // Previous stage
    if (currentStage > 0) {
      visibleStages.push({
        ...stages[currentStage - 1],
        position: 'previous' as const,
        index: currentStage - 1
      });
    }
    
    // Current stage
    if (currentStage < stages.length) {
      visibleStages.push({
        ...stages[currentStage],
        position: 'current' as const,
        index: currentStage
      });
    }
    
    // Next stage
    if (currentStage + 1 < stages.length) {
      visibleStages.push({
        ...stages[currentStage + 1],
        position: 'next' as const,
        index: currentStage + 1
      });
    }
    
    return visibleStages;
  };

  const visibleStages = getVisibleStages();
  
  return stages && (
    <Card className="w-full border-0 shadow-lg">
      <CardContent className="flex flex-col items-center justify-center py-5 px-5">
        <div className="mb-4">
          <div className="relative">
            <LoaderCircle className="h-16 w-16 text-primary animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-8 w-8 bg-primary/20 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
        
        <p className="text-lg font-bold mb-2 text-slate-900">{title}</p>
        <p className="text-slate-600 text-center mb-8 max-w-md">
          {description}
        </p>
        
       <div className="relative w-full max-w-lg h-96 overflow-hidden">
          <div className="absolute inset-0 space-y-6">
            {visibleStages.map((stage, stageIndex) => {
              const isCompleted = completedStages.includes(stage.id);
              const isCurrent = stage.position === 'current';
              const isPrevious = stage.position === 'previous';
              const isNext = stage.position === 'next';
              
              // Calculate transform based on position
              let translateY = 0;
              if (isPrevious) translateY = 0;
              if (isCurrent) translateY = 90;
              if (isNext) translateY = 240;
              
              return (
                <div 
                  key={`${stage.id}-${stage.index}`}
                  className={`absolute w-full transition-all duration-700 ease-in-out ${
                    isCurrent ? 'scale-105 z-20' : isPrevious ? 'scale-95 z-10' : 'scale-90 z-0'
                  } ${
                    isPrevious ? 'opacity-60' : isCurrent ? 'opacity-100' : 'opacity-20'
                  }`}
                  style={{ 
                    transform: `translateY(${translateY}px)`,
                    transition: 'all 0.7s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 ${
                      isCompleted 
                        ? 'bg-green-500 text-white shadow-lg' 
                        : isCurrent 
                          ? 'bg-primary text-white animate-pulse shadow-lg' 
                          : 'bg-slate-200 text-slate-500'
                    }`}>
                      {isCompleted ? (
                        <Check className="h-6 w-6" />
                      ) : (
                        <stage.icon className={`h-6 w-6 ${isCurrent ? 'animate-bounce' : ''}`} />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-semibold text-lg transition-colors duration-300 ${
                        isCompleted 
                          ? 'text-green-700' 
                          : isCurrent 
                            ? 'text-primary' 
                            : 'text-slate-700'
                      }`}>
                        {stage.title}
                      </h4>
                      <p className={`text-sm mt-2 transition-colors duration-300 ${
                        isCompleted 
                          ? 'text-green-600' 
                          : isCurrent 
                            ? 'text-primary/80' 
                            : 'text-slate-500'
                      }`}>
                        {stage.description}
                      </p>
                      
                      
                      {isCurrent && (
                        <div className="mt-4 space-y-2">
                          <ProgressBar 
                            noColors={true}
                            hideNumber={true}
                            progress={Math.round(progress)} 
                            className="w-full h-2 bg-slate-200"
                          />
                          <div className="flex justify-between text-xs text-primary/70">
                            <span>Processing...</span>
                            <span>{Math.round(progress)}%</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className={`flex-shrink-0 transition-all duration-300 ${
                      isCompleted 
                        ? 'text-green-600' 
                        : isCurrent 
                          ? 'text-primary animate-pulse' 
                          : 'text-slate-400'
                    }`}>
                      {isCompleted && (
                        <div className="text-sm font-medium">Done</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-sm text-slate-500">
            {estimate}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};